import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface UseSmartPositioningProps {
	isOpen: boolean;
	containerRef: React.RefObject<HTMLDivElement>;
	buffer?: number; // Buffer space from viewport edges
}

interface UseSmartPositioningReturn {
	dropdownRef: React.RefObject<HTMLDivElement>;
	position: "above" | "below";
	maxHeight: number;
	isPositioned: boolean;
}

/**
 * Enhanced positioning hook with better viewport detection and responsive sizing
 */
export const useSmartPositioning = ({
	isOpen,
	containerRef,
	buffer = 20,
}: UseSmartPositioningProps): UseSmartPositioningReturn => {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<"above" | "below">("below");
	const [maxHeight, setMaxHeight] = useState(200);
	const [isPositioned, setIsPositioned] = useState(false);

	const calculatePosition = useCallback(() => {
		if (!containerRef.current) {
			return;
		}

		const container = containerRef.current;
		const containerRect = container.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// Calculate available space above and below
		const spaceAbove = containerRect.top - buffer;
		const spaceBelow = viewportHeight - containerRect.bottom - buffer;

		// Determine position based on available space
		const shouldShowAbove = spaceBelow < 150 && spaceAbove > spaceBelow;
		const newPosition = shouldShowAbove ? "above" : "below";

		// Calculate max height based on available space
		const availableSpace = shouldShowAbove ? spaceAbove : spaceBelow;
		const newMaxHeight = Math.max(100, Math.min(300, availableSpace - 10));

		// Batch state updates to avoid multiple re-renders
		setPosition(newPosition);
		setMaxHeight(newMaxHeight);
		setIsPositioned(true);
	}, [containerRef, buffer]);

	// Calculate position immediately when dropdown opens (before paint)
	useLayoutEffect(() => {
		if (isOpen) {
			setIsPositioned(false); // Reset positioning state
			calculatePosition();
		} else {
			setIsPositioned(false);
		}
	}, [isOpen, calculatePosition]);

	// Recalculate position on scroll/resize
	useEffect(() => {
		if (isOpen && isPositioned) {
			const handleScrollResize = () => {
				calculatePosition();
			};

			window.addEventListener("scroll", handleScrollResize, { passive: true });
			window.addEventListener("resize", handleScrollResize, { passive: true });

			return () => {
				window.removeEventListener("scroll", handleScrollResize);
				window.removeEventListener("resize", handleScrollResize);
			};
		}
	}, [isOpen, isPositioned, calculatePosition]);

	return {
		dropdownRef,
		position,
		maxHeight,
		isPositioned,
	};
};
